(ns ycweb.app.pos.integration.common
  (:require
   [ycweb.util.core :as u]
   [ycweb.util.comms :as comms]
   #?@(:clj
       [[cheshire.core :as json]
        [clj-time.core :as dt]]
       :cljs
       [[cljs-time.core :as dt]])
   [ycweb.components.toaster :as toaster]
   [ycweb.components.versioncheck :as versioncheck]
   [ycweb.util.date :as ud]))

(declare pebble-response)

(defn json [m]
  #?(:clj
     (json/generate-string m)
     :cljs
     (.stringify js/JSON
       (clj->js m))))

(defn json-parse [m]
  #?(:clj
     (json/parse-string m)
     :cljs
     (js->clj (.parse js/JSON m) :keywordize-keys true)))

(def ^:private safe-response-keys
  "Whitelist of terminal-response fields that are safe to log. Deliberately EXCLUDES
   receiptBundle (PAN/BIN/last4/PAN_HASH/cardholder name/EMV cryptograms/PIN data) and any
   other card-bearing field — never log the raw response or receiptBundle. PCI-DSS."
  [:launchType :resultCode :resultDescription :transactionUuid :transactionAmount
   :outstandingAmount :merchantID :serialNumber :errorBundle])

(defn safe-response
  "Redact a terminal response to PCI-safe fields for logging."
  [response]
  (select-keys response safe-response-keys))

(defn comms-msg
  "Merge the integration's terminal credentials into a message map and JSON-encode it."
  [config opts]
  (let [result
        (merge opts
          {:applicationKey (:api-key config)
           :merchantID (:merchant-id config)
           :merchantUsername (:merchant-username config)})]
    (json result)))

(defn make-retail-auth-handler
  "Step 2c — comms-method response handler: on receiving auth credentials, launch the
   terminal's RETAIL_AUTH call with the access/secret keys."
  [intg]
  (fn [app-atom config]
    #?(:cljs
       (let [{:keys [state-key launch-payment]} intg
             message {:launchType "RETAIL_AUTH"}]
         (comms/log "[STEP 2c] make-retail-auth-handler")
         (swap! app-atom assoc-in [state-key :request] message)
         (toaster/close-toast app-atom)
         (launch-payment
           (comms-msg config
             (assoc message
               :accessKey (:access-token config)
               :secretKey (:secret-key config))))))))

(defn initialize
  "Step 2b — initialise the terminal with the stored authentication key."
  [intg app-atom config]
  #?(:cljs
     (let [{:keys [state-key init-payment]} intg]
       (comms/log "[STEP 2b] initialize")
       (swap! app-atom assoc-in [state-key :init] true)
       (init-payment
         (comms-msg config
           {:authenticationKey (u/get-local-storage :pebble-authkey)})))))

(defn retail-auth
  "Step 2a — request retail-auth credentials from the server via the integration's comms-method."
  [intg app-atom config]
  #?(:cljs
     (let [{:keys [auth-label comms-method]} intg]
       (comms/log "[STEP 2a] retail-auth")
       (toaster/open-toast app-atom :processing auth-label {})
       (comms/send comms-method {}))))

(defn auth [intg app-atom config]
  #?(:cljs
     (retail-auth intg app-atom config)))

(defn launch-sale
  "Step 4 (sale) — build and launch the SALE / QR_CODE_PAYMENT terminal message (or emulate)."
  [intg app-atom config launch-type {:keys [amount reference id payment-uuid emulate] :as sale}]
  #?(:cljs
     (let [{:keys [state-key current-action emulate-messages launch-payment send-payment-uuid?]} intg
           qr? (= launch-type "QR_CODE_PAYMENT")
           message
           (cond-> {:launchType launch-type
                    :authenticationKey (u/get-local-storage :pebble-authkey)
                    :transactionAmount amount
                    :transactionReferenceNo reference
                    :transactionDescription reference
                    :isReceiptRequired false
                    :alwaysShowTransactionStatusScreen false}

             (and send-payment-uuid? payment-uuid)
             (assoc
               :transactionUuid payment-uuid
               :externalTransactionGUID payment-uuid)

             qr?
             (assoc
               :externalInvoiceGUID (str payment-uuid)))]
       (comms/log (pr-str "[STEP 4] launch-sale" {:launch-type launch-type :amount amount :reference reference :payment-uuid payment-uuid :emulate emulate}))
       (swap! app-atom assoc-in [state-key :request] message)

       (if emulate
         (do
           (swap! current-action assoc :emulate-response
             (assoc ((get emulate-messages emulate) sale) :launchType launch-type))
           (pebble-response intg app-atom))

         (launch-payment
           (comms-msg config message))))))

(defn handle-sale-response
  "Step 6 (sale/status) — apply the integration's approved? + match-reference? predicates and
   fire success-fn (with amount/reference), error-fn, or fail-fn accordingly."
  [{:keys [approved? match-reference? send-payment-uuid?]} {:keys [type config data success-fn fail-fn error-fn] :as action} response]
  (let [merged-error (apply merge (:errorBundle response))
        reason
        (or
          (u/presence (:message merged-error))
          (u/presence (:description merged-error))
          (:resultDescription response))]
    (comms/log (pr-str "[STEP 6] handle-sale-response" {:approved? (approved? response) :match-reference? (match-reference? data response) :resultCode (:resultCode response) :reason reason}))
    (cond
      (and
        (match-reference? data response)
        (approved? response))
      (success-fn
        {:amount (u/parseInt (:transactionAmount response))
         :reference (if (and send-payment-uuid? (:payment-uuid data))
                      (:payment-uuid data)
                      (:transactionUuid response))})

      (and error-fn (:errorBundle response)
        (not= "TRANSACTION_NOT_FOUND" (:errorType merged-error)))
      (error-fn reason)

      :else
      (fail-fn reason))))

(defn fallback-emulate [{:keys [device-fn]} app-atom]
  (if (and (versioncheck/dev-enabled app-atom) (not (device-fn)))
    :success nil))

(defn launch-status-check
  "Step 4 (status) — build and launch the TX_SEARCH terminal message (or emulate)."
  [intg app-atom config {:keys [amount reference id payment-uuid emulate] :as sale}]
  #?(:cljs
     (let [{:keys [state-key current-action emulate-messages launch-payment send-payment-uuid?]} intg
           message
           (cond-> {:launchType "TX_SEARCH"
                    :authenticationKey (u/get-local-storage :pebble-authkey)}

             (and send-payment-uuid? payment-uuid)
             (assoc :transactionUuid payment-uuid)

             (not (and send-payment-uuid? payment-uuid))
             (assoc :transactionAmount amount
                    :transactionDescription reference))]
       (comms/log (pr-str "[STEP 4] launch-status-check" {:amount amount :reference reference :payment-uuid payment-uuid :emulate emulate}))
       (swap! app-atom assoc-in [state-key :request] message)

       (if emulate
         (do
           (swap! current-action assoc :emulate-response
             (assoc ((get emulate-messages emulate) sale)
               :launchType "TX_SEARCH"))
           (pebble-response intg app-atom))

         (launch-payment
           (comms-msg config message))))))

(defn launch-refund
  "Step 4 (refund) — build the REFUND terminal message and launch it (or emulate)."
  [intg app-atom config {:keys [amount reference uuid emulate] :as sale}]
  #?(:cljs
     (let [{:keys [state-key current-action emulate-messages launch-payment]} intg
           message
           {:launchType "REFUND"
            :authenticationKey (u/get-local-storage :pebble-authkey)
            :transactionAmount amount
            :originalTransactionUuid uuid
            :transactionDescription (str "R" reference)
            :isReceiptRequired false
            :alwaysShowTransactionStatusScreen false}]

       (comms/log (pr-str "[STEP 4] launch-refund" {:amount amount :reference reference :uuid uuid :emulate emulate}))
       (swap! app-atom assoc-in [state-key :request] message)
       (if emulate
         (do
           (swap! current-action assoc :emulate-response
             ((get emulate-messages :refund) sale))
           (pebble-response intg app-atom))

         (launch-payment
           (comms-msg config message))))))

(defn handle-refund-response
  "Step 6 (refund) — apply approved? and fire success-fn, error-fn or fail-fn."
  [{:keys [approved?]} {:keys [type config data success-fn fail-fn error-fn] :as action} response]
  (let [merged-error (apply merge (:errorBundle response))
        reason
        (or
          (u/presence (:message merged-error))
          (u/presence (:description merged-error))
          (:resultDescription response))]
    (comms/log (pr-str "[STEP 6] handle-refund-response" {:approved? (approved? response) :errorType (:errorType merged-error) :reason reason}))
    (cond
      (approved? response)
      (success-fn
        {:amount (:transactionAmount response)
         :reference (:originalTransactionUuid response)})

      (and error-fn (:errorBundle response)
        (not= "TRANSACTION" (:errorType merged-error)))
      (error-fn reason)

      :else
      (fail-fn reason))))

(defn run-action
  "Step 3 — dispatch the current action to the matching launch-* (sale/refund/check-status)."
  [intg app-atom {:keys [type config data] :as action}]
  (comms/log (pr-str "[STEP 3] run-action" {:type type}))
  (case type
    :sale (launch-sale intg app-atom config "SALE" data)
    :qr-sale (launch-sale intg app-atom config "QR_CODE_PAYMENT" data)
    :refund (launch-refund intg app-atom config data)
    :check-status (launch-status-check intg app-atom config data)

    (toaster/open-toast app-atom :error (str "Payment action " type " not supported") {})))

(defn pebble-response
  "Step 5 — parse the terminal callback, refresh the stored auth key, then route by launchType
   to the step-6 handlers."
  ([intg app-atom] (pebble-response intg app-atom nil))
  ([intg app-atom raw-response]
   #?(:cljs
      (let [{:keys [state-key parse-response current-action get-response]} intg
            {:keys [type config data success-fn fail-fn error-fn emulate-response] :as action}
            @current-action]
        (comms/log (pr-str "[STEP 5] pebble-response" {:action-type type :emulating? (some? emulate-response)}))
        (try
          (let [raw-response
                (when-not emulate-response
                  (or raw-response (get-response)))

                response
                (or emulate-response
                  (let [parsed (first (json-parse raw-response))]
                    (apply merge
                      (parse-response parsed))))

                merged-error (apply merge (:errorBundle response))

                error-present?
                (and
                  (u/presence (:errorType merged-error))
                  (u/presence (:reference merged-error))
                  (u/presence (:message merged-error)))]

            ;; PCI: store only redacted response in app-atom — receiptBundle (PAN/cardholder/EMV)
            ;; must never land in client state (it can be read via devtools, or persisted to
            ;; localStorage by the dev "Save state" tool). Nothing reads the stored response.
            (swap! app-atom assoc-in [state-key :response] (safe-response response))
            (comms/log (pr-str "[STEP 5] response" (safe-response response)))

            (cond
              (and
                (= (:launchType response) "RETAIL_AUTH")
                (= (:resultCode response) "00")
                (:authenticationKey response))
              (do
                (u/set-local-storage :pebble-authkey (:authenticationKey response))
                (u/set-local-storage :pebble-authkey-date (ud/millis (dt/now))))

              (or
                (and error-present? (= (:launchType response) "RETAIL_AUTH"))
                (and error-present? (= (:errorType merged-error) "AUTHENTICATION")))
              (do
                (u/set-local-storage :pebble-authkey "null")
                (u/set-local-storage :pebble-authkey-date nil)))

            (case (:launchType response)
              "RETAIL_AUTH"
              (do
                (initialize intg app-atom config)
                (when type
                  (run-action intg app-atom action)))

              "SALE"
              (handle-sale-response intg action response)

              "QR_CODE_PAYMENT"
              (handle-sale-response intg action response)

              "REFUND"
              (handle-refund-response intg action response)

              "TX_SEARCH"
              (handle-sale-response intg action response)

              ((or error-fn fail-fn)
               (or
                 (u/presence (:description merged-error))
                 (u/presence (:message merged-error))
                 (:resultDescription response)
                 "No idea."))))
          (catch js/Object e
            (swap! app-atom assoc-in [state-key :exception] (str e))
            (comms/log "[STEP 5] exception")
            (comms/log (str e))))))))

(defn handle-call
  "Step 1 — payment-flow entry point: set current-action, then run the action if a valid auth
   key is cached, otherwise request retail auth first."
  [intg app-atom calltype sale
   success-fn fail-fn & [error-fn]]
  (let [{:keys [config-key current-action]} intg
        config (get-in @app-atom [:config config-key])
        emulate (or (:emulate sale) (fallback-emulate intg app-atom))]

    (comms/log (pr-str "[STEP 1] handle-call" {:calltype calltype :sale sale :emulate emulate}))

    (reset! current-action
      {:config config :type calltype :data (assoc sale :emulate emulate)
       :success-fn success-fn :fail-fn fail-fn :error-fn error-fn})

    (if emulate
      (do
        (println "Emulating payment: " emulate)
        (run-action intg app-atom @current-action))

      (let [authkey (u/get-local-storage :pebble-authkey)
            pebble-authkey-date (u/get-local-storage :pebble-authkey-date)
            authkey-date
            (some->
              (u/get-local-storage :pebble-authkey-date)
              (u/parseInt)
              (ud/from-millis))]

        (cond
          (and
            (u/presence pebble-authkey-date) (dt/after? authkey-date (dt/minus (dt/now) (dt/months 1)))
            (u/presence authkey) (not= authkey "null"))
          (do
            (println "Authkey exists")
            (initialize intg app-atom config)
            (run-action intg app-atom @current-action))

          :else
          (do
            (println "Run Auth first")
            (auth intg app-atom config)))))))

(defn init-router
  "Install the window.paymentPebbleResponse terminal callback and the host error/log bridges."
  [intg app-atom]
  #?(:cljs
     (when ((:device-fn intg))
       (println "Setting up payment pebble router")

       (set! (.-paymentPebbleResponse js/window)
         (fn []
           (pebble-response intg app-atom)))

       (set! (.-logError js/window)
         (fn []
           (let [m ((:get-error intg))]
             (swap! app-atom assoc-in [(:state-key intg) :host-error] m)
             (comms/log (str "Host Exception: " m)))))

       (set! (.-log js/window)
         (fn []
           (let [m ((:get-log intg))]
             (swap! app-atom assoc-in [(:state-key intg) :host-log] m)
             (comms/log (str "Host Log: " m))))))))